import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Select } from '../../../components/ui/Select';
import {
  useLoanSimulation,
  useApplicablePolicy,
  useRequestLoan,
} from '../../../hooks/useCredit';
import type { CreditProductType, AmortizationType, SimulationResult } from '../../../types/credit';

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value / 100);
};

const productTypeOptions = [
  { value: 'PERSONAL_LOAN', label: 'Empréstimo Pessoal' },
  { value: 'PAYROLL_LOAN', label: 'Crédito Consignado' },
  { value: 'WORKING_CAPITAL', label: 'Capital de Giro' },
];

const amortizationOptions = [
  { value: 'PRICE', label: 'Price (Parcelas Fixas)' },
  { value: 'SAC', label: 'SAC (Parcelas Decrescentes)' },
];

const purposeOptions = [
  { value: 'DEBT_CONSOLIDATION', label: 'Consolidação de Dívidas' },
  { value: 'HOME_IMPROVEMENT', label: 'Reforma/Melhoria Residencial' },
  { value: 'EDUCATION', label: 'Educação' },
  { value: 'HEALTH', label: 'Saúde' },
  { value: 'VEHICLE', label: 'Veículo' },
  { value: 'TRAVEL', label: 'Viagem' },
  { value: 'BUSINESS', label: 'Negócio' },
  { value: 'OTHER', label: 'Outro' },
];

type Step = 'simulation' | 'details' | 'review' | 'submitted';

export function LoanRequest() {
  const [step, setStep] = useState<Step>('simulation');
  const [productType, setProductType] = useState<CreditProductType>('PERSONAL_LOAN');
  const [amount, setAmount] = useState(500000);
  const [installments, setInstallments] = useState(12);
  const [amortizationType, setAmortizationType] = useState<AmortizationType>('PRICE');
  const [purpose, setPurpose] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [simulation, setSimulation] = useState<SimulationResult | null>(null);

  const { data: policy } = useApplicablePolicy(productType);
  const simulateLoan = useLoanSimulation();
  const requestLoan = useRequestLoan();

  useEffect(() => {
    if (policy) {
      if (amount < policy.minAmount) setAmount(policy.minAmount);
      if (amount > policy.maxAmount) setAmount(policy.maxAmount);
      if (installments < policy.minInstallments) setInstallments(policy.minInstallments);
      if (installments > policy.maxInstallments) setInstallments(policy.maxInstallments);
    }
  }, [policy]);

  const handleSimulate = async () => {
    try {
      const result = await simulateLoan.mutateAsync({
        productType,
        amount,
        installments,
        amortizationType,
      });
      setSimulation(result);
      setStep('details');
    } catch (error) {
      console.error('Erro na simulação:', error);
    }
  };

  const handleSubmit = async () => {
    if (!simulation || !acceptedTerms) return;

    try {
      await requestLoan.mutateAsync({
        productType,
        requestedAmount: amount,
        installmentCount: installments,
        amortizationType,
      });
      setStep('submitted');
    } catch (error) {
      console.error('Erro ao solicitar:', error);
    }
  };

  // Step 1: Simulação
  if (step === 'simulation') {
    return (
      <div className="max-w-2xl mx-auto space-y-6">
        <h1 className="text-2xl font-bold">Solicitar Empréstimo</h1>

        <Card>
          <CardHeader>
            <CardTitle>Passo 1: Configure seu Empréstimo</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Select
              label="Tipo de Empréstimo"
              value={productType}
              onChange={(e) => setProductType(e.target.value as CreditProductType)}
              options={productTypeOptions}
            />

            <div>
              <label className="block text-sm font-medium mb-2">
                Valor: {formatCurrency(amount)}
              </label>
              <input
                type="range"
                min={policy?.minAmount || 50000}
                max={policy?.maxAmount || 10000000}
                step={10000}
                value={amount}
                onChange={(e) => setAmount(Number(e.target.value))}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-500">
                <span>{formatCurrency(policy?.minAmount || 50000)}</span>
                <span>{formatCurrency(policy?.maxAmount || 10000000)}</span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Parcelas: {installments}x
              </label>
              <input
                type="range"
                min={policy?.minInstallments || 3}
                max={policy?.maxInstallments || 48}
                value={installments}
                onChange={(e) => setInstallments(Number(e.target.value))}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-500">
                <span>{policy?.minInstallments || 3}x</span>
                <span>{policy?.maxInstallments || 48}x</span>
              </div>
            </div>

            <Select
              label="Tipo de Amortização"
              value={amortizationType}
              onChange={(e) => setAmortizationType(e.target.value as AmortizationType)}
              options={amortizationOptions}
            />

            <Button
              onClick={handleSimulate}
              disabled={simulateLoan.isPending}
              className="w-full"
            >
              {simulateLoan.isPending ? 'Calculando...' : 'Continuar'}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Step 2: Detalhes
  if (step === 'details' && simulation) {
    return (
      <div className="max-w-2xl mx-auto space-y-6">
        <h1 className="text-2xl font-bold">Solicitar Empréstimo</h1>

        <Card>
          <CardHeader>
            <CardTitle>Passo 2: Resumo da Simulação</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-blue-50 p-4 rounded-lg text-center">
              <p className="text-sm text-gray-600">Parcela Mensal</p>
              <p className="text-3xl font-bold text-blue-600">
                {formatCurrency(simulation.installmentAmount)}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-500">Valor Solicitado</p>
                <p className="font-medium">{formatCurrency(simulation.amount)}</p>
              </div>
              <div>
                <p className="text-gray-500">Total a Pagar</p>
                <p className="font-medium">{formatCurrency(simulation.totalAmount)}</p>
              </div>
              <div>
                <p className="text-gray-500">Total de Juros</p>
                <p className="font-medium">{formatCurrency(simulation.totalInterest)}</p>
              </div>
              <div>
                <p className="text-gray-500">IOF</p>
                <p className="font-medium">{formatCurrency(simulation.totalIOF)}</p>
              </div>
              <div>
                <p className="text-gray-500">Taxa Mensal</p>
                <p className="font-medium">
                  {(simulation.monthlyInterestRate * 100).toFixed(2)}%
                </p>
              </div>
              <div>
                <p className="text-gray-500">CET (a.a.)</p>
                <p className="font-medium text-lg">{simulation.cet.toFixed(2)}%</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Finalidade do Empréstimo</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Select
              label="Para que você precisa do dinheiro?"
              value={purpose}
              onChange={(e) => setPurpose(e.target.value)}
              options={[{ value: '', label: 'Selecione...' }, ...purposeOptions]}
            />

            <div className="flex gap-2">
              <Button
                variant="secondary"
                onClick={() => setStep('simulation')}
                className="flex-1"
              >
                Voltar
              </Button>
              <Button
                onClick={() => setStep('review')}
                disabled={!purpose}
                className="flex-1"
              >
                Continuar
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Step 3: Revisão e Termos
  if (step === 'review' && simulation) {
    return (
      <div className="max-w-2xl mx-auto space-y-6">
        <h1 className="text-2xl font-bold">Solicitar Empréstimo</h1>

        <Card>
          <CardHeader>
            <CardTitle>Passo 3: Revisão e Confirmação</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-gray-50 p-4 rounded-lg space-y-2">
              <div className="flex justify-between">
                <span>Tipo:</span>
                <span className="font-medium">
                  {productTypeOptions.find((p) => p.value === productType)?.label}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Valor:</span>
                <span className="font-medium">{formatCurrency(amount)}</span>
              </div>
              <div className="flex justify-between">
                <span>Parcelas:</span>
                <span className="font-medium">{installments}x de {formatCurrency(simulation.installmentAmount)}</span>
              </div>
              <div className="flex justify-between">
                <span>Total:</span>
                <span className="font-medium">{formatCurrency(simulation.totalAmount)}</span>
              </div>
              <div className="flex justify-between">
                <span>CET:</span>
                <span className="font-medium">{simulation.cet.toFixed(2)}% a.a.</span>
              </div>
              <div className="flex justify-between">
                <span>Finalidade:</span>
                <span className="font-medium">
                  {purposeOptions.find((p) => p.value === purpose)?.label}
                </span>
              </div>
            </div>

            <div className="border rounded-lg p-4 max-h-48 overflow-y-auto text-sm text-gray-600">
              <h4 className="font-medium text-gray-900 mb-2">Termos e Condições</h4>
              <p className="mb-2">
                Ao solicitar este empréstimo, você declara que:
              </p>
              <ul className="list-disc pl-4 space-y-1">
                <li>Todas as informações fornecidas são verdadeiras e completas.</li>
                <li>Você tem capacidade financeira para arcar com as parcelas.</li>
                <li>Autoriza a consulta aos órgãos de proteção ao crédito.</li>
                <li>Concorda com a taxa de juros e CET informados.</li>
                <li>Está ciente de que o não pagamento pode resultar em negativação.</li>
                <li>O contrato será enviado para assinatura digital após aprovação.</li>
              </ul>
              <p className="mt-2">
                O valor será creditado em sua conta após a assinatura do contrato e
                análise de crédito. O prazo de análise é de até 2 dias úteis.
              </p>
            </div>

            <div className="flex items-start gap-2">
              <input
                type="checkbox"
                id="terms"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                className="mt-1"
              />
              <label htmlFor="terms" className="text-sm">
                Li e aceito os termos e condições do empréstimo, incluindo a taxa de
                juros, CET e demais encargos.
              </label>
            </div>

            <div className="flex gap-2">
              <Button
                variant="secondary"
                onClick={() => setStep('details')}
                className="flex-1"
              >
                Voltar
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={!acceptedTerms || requestLoan.isPending}
                className="flex-1"
              >
                {requestLoan.isPending ? 'Enviando...' : 'Solicitar Empréstimo'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Step 4: Solicitação Enviada
  if (step === 'submitted') {
    return (
      <div className="max-w-2xl mx-auto space-y-6">
        <Card>
          <CardContent className="py-12 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">✓</span>
            </div>
            <h2 className="text-2xl font-bold mb-2">Solicitação Enviada!</h2>
            <p className="text-gray-500 mb-6">
              Sua solicitação de empréstimo foi recebida e está em análise.
              Você receberá uma notificação assim que tivermos uma resposta.
            </p>
            <div className="bg-gray-50 p-4 rounded-lg mb-6">
              <p className="text-sm text-gray-500">Prazo de análise</p>
              <p className="font-medium">Até 2 dias úteis</p>
            </div>
            <div className="flex gap-2 justify-center">
              <Button
                variant="secondary"
                onClick={() => (window.location.href = '/banking/credit/loans')}
              >
                Ver Meus Empréstimos
              </Button>
              <Button onClick={() => (window.location.href = '/banking/customer')}>
                Voltar ao Início
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return null;
}

export default LoanRequest;
